<template>
  <div class="settings-locations-section">
    <SettingsSectionHeader
      title="Locations"
      subtitle="Offices and work sites"
      icon="pi-map-marker"
    >
      <template #actions>
        <Button
          label="Add"
          icon="pi pi-plus"
          size="small"
          :disabled="mutating"
          @click="openCreate"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadLocations"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-locations-section__body">
      <div v-if="loading && !locations.length" class="settings-locations-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-locations-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadLocations" />
        </div>
      </Message>

      <template v-else>
        <SettingsOrgReferenceToolbar
          v-model:show-inactive="showInactive"
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          inactive-input-id="locations-show-inactive"
          search-placeholder="Search locations..."
          :loading="loading"
        />

        <div
          v-if="!filteredLocations.length"
          class="settings-locations-section__empty"
        >
          <i class="pi pi-map-marker settings-locations-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredLocations"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          :sort-field="sortField"
          :sort-order="sortOrder"
          @sort="onSort"
          class="settings-locations-section__table"
          :rowClass="rowClass"
        >
          <Column field="name" header="Name" sortable style="min-width: 9rem">
            <template #body="{ data }">
              <span class="font-medium">{{ data.name }}</span>
            </template>
          </Column>
          <Column v-if="showInactive" header="Status" style="width: 6.5rem">
            <template #body="{ data }">
              <SettingsOrgReferenceStatusTag :item="data" />
            </template>
          </Column>
          <Column field="type" header="Type" sortable style="width: 8rem">
            <template #body="{ data }">
              <Tag
                :value="getLocationTypeLabel(data.type)"
                severity="secondary"
                class="!text-xs"
              />
            </template>
          </Column>
          <Column header="Country" style="min-width: 8rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ formatCountry(data.country) }}
              </span>
            </template>
          </Column>
          <Column header="Place" style="min-width: 9rem">
            <template #body="{ data }">
              <span
                class="text-sm"
                :class="formatPlace(data) ? 'text-surface-600 dark:text-surface-400' : 'text-surface-400 dark:text-surface-500'"
              >
                {{ formatPlace(data) || 'Not set' }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  aria-label="Edit"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <Button
                  v-if="isOrgReferenceActive(data)"
                  icon="pi pi-ban"
                  size="small"
                  severity="warn"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Deactivate'"
                  aria-label="Deactivate"
                  :disabled="mutating"
                  @click="confirmDeactivate(data)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Delete'"
                  aria-label="Delete"
                  :disabled="mutating"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <LocationEditModal
      ref="editModalRef"
      v-model:visible="editModalVisible"
      :saving="mutating"
      :save-error="saveError"
      :locations="catalogLocations"
      @save="handleSave"
    />
  </div>
</template>

<script setup>
import { useSettingsDraftGuard } from '../../composables/useSettingsDraftGuard';
import { matchesSearchText } from '@/utils/searchText';
import { useSettingsLoadGuard } from '../../composables/useSettingsLoadGuard';
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import LocationEditModal from '../modals/LocationEditModal.vue';
import { useSettingsOrgReferenceList } from '../../composables/useSettingsOrgReferenceList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import { fetchOrgReferenceOptions, orgReferenceApi } from '@/api/orgReferenceApi';
import { getLocationTypeLabel } from '@/constants/orgReference';
import { countryOptionLabel, normalizeCountry } from '@/utils/countryCatalog';
import { useClientTableSort } from '@/utils/tableSort';
import { resolveLocationCityState } from '@/features/org-references/utils/referenceOptionUtils';

defineOptions({ name: 'SettingsLocationsSection' });

const { sortField, sortOrder, onSort } = useClientTableSort('name');

defineProps({
  section: {
    type: Object,
    default: null,
  },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { showInactive, fetchOptions, afterMutation } = useSettingsOrgReferenceList();

const locations = ref([]);
/** Active + inactive work sites so country delete cannot miss a hidden row. */
const catalogLocations = ref([]);
const loading = ref(false);
const beginLoad = useSettingsLoadGuard();
const error = ref('');
const mutating = ref(false);
const saveError = ref(null);
const searchQuery = ref('');
const editModalVisible = ref(false);
const editModalRef = ref(null);

const filteredLocations = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = query
    ? locations.value.filter((location) => {
        const inferred = resolveLocationCityState(location);
        const haystack = [
          location.name,
          location.type,
          location.city,
          location.state,
          inferred.city,
          inferred.state,
          location.address,
          location.country,
          getLocationTypeLabel(location.type),
          isOrgReferenceActive(location) ? 'active' : 'inactive',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return matchesSearchText([haystack], query);
      })
    : locations.value;
  return sortOrgReferenceRows(base);
});

const countLabel = computed(() =>
  buildOrgReferenceCountLabel(locations.value, 'locations', showInactive.value)
);

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No locations match your search.';
  if (showInactive.value) return 'No locations found. Click Add to create one.';
  return 'No active locations yet. Click Add to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const formatCountry = (value) => {
  const canonical = normalizeCountry(value);
  return canonical ? countryOptionLabel(canonical) : '—';
};

const formatPlace = (location) => {
  const street = String(location.address ?? '').replace(/\s+/g, ' ').trim();
  const { city, state } = resolveLocationCityState(location);
  const parts = [street, city, state].filter(Boolean);
  return parts.length ? parts.join(', ') : '';
};

const notifyMutationSuccess = (detail) => {
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail,
    life: 2500,
  });
};

const notifyError = (err, fallback) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: err?.message || fallback,
    life: 3500,
  });
};

const afterMutationReload = () => afterMutation(loadLocations);

const loadLocations = async () => {
  const isCurrent = beginLoad();
  loading.value = true;
  error.value = '';
  try {
    const options = await fetchOptions();
    if (!isCurrent()) return;
    locations.value = Array.isArray(options?.locations)
      ? sortOrgReferenceRows(options.locations)
      : [];
    if (showInactive.value) {
      catalogLocations.value = locations.value;
    } else {
      try {
        const allOptions = await fetchOrgReferenceOptions({ force: true, includeInactive: true });
        if (!isCurrent()) return;
        catalogLocations.value = Array.isArray(allOptions?.locations)
          ? allOptions.locations
          : locations.value;
      } catch {
        if (!isCurrent()) return;
        catalogLocations.value = locations.value;
      }
    }
  } catch (err) {
    if (!isCurrent()) return;
    error.value = err?.message || 'Failed to load locations.';
    locations.value = [];
    catalogLocations.value = [];
  } finally {
    if (isCurrent()) loading.value = false;
  }
};

const openCreate = () => {
  saveError.value = null;
  editModalRef.value?.openNew?.();
  editModalVisible.value = true;
};

const openEdit = (location) => {
  saveError.value = null;
  editModalRef.value?.openEdit?.(location);
  editModalVisible.value = true;
};

const handleSave = async ({ mode, payload }) => {
  if (mutating.value) return;
  saveError.value = null;
  mutating.value = true;
  try {
    if (mode === 'new') {
      await orgReferenceApi.locations.create(payload);
      notifyMutationSuccess('Location created');
    } else {
      await orgReferenceApi.locations.update(payload);
      notifyMutationSuccess('Location updated');
    }
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    saveError.value = { message: err?.message || 'Failed to save changes.' };
    notifyError(err, mode === 'new' ? 'Failed to create location.' : 'Failed to update location.');
  } finally {
    mutating.value = false;
  }
};

const confirmDeactivate = (location) => {
  confirm.require({
    header: 'Deactivate location',
    message: `"${location.name}" will be hidden from profile options. Employees and teams linked to this location keep their records.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateLocation(location),
  });
};

const deactivateLocation = async (location) => {
  if (mutating.value) return;
  mutating.value = true;
  try {
    await orgReferenceApi.locations.update({
      location_id: location.id,
      is_active: 0,
    });
    notifyMutationSuccess('Location deactivated');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to deactivate location.');
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (location) => {
  confirm.require({
    header: 'Delete location',
    message: `Delete "${location.name}"? Employee and team location links will be cleared. This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteLocation(location),
  });
};

const deleteLocation = async (location) => {
  if (mutating.value) return;
  mutating.value = true;
  try {
    await orgReferenceApi.locations.delete({ location_id: location.id });
    notifyMutationSuccess('Location deleted');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to delete location.');
  } finally {
    mutating.value = false;
  }
};

onMounted(() => {
  loadLocations();
});

watch(showInactive, () => {
  loadLocations();
});
useSettingsDraftGuard({ draft: {}, saving: () => mutating.value });
</script>

<style scoped>
.settings-locations-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-locations-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-locations-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-locations-section__message {
  margin: 0;
}

.settings-locations-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-locations-section__empty-icon {
  font-size: 1.5rem;
}

.settings-locations-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

:deep(.settings-org-section__row--inactive) {
  opacity: 0.65;
}
</style>
